import React from 'react';
import { FiX, FiMail, FiMessageSquare, FiChevronRight, FiCheckCircle } from 'react-icons/fi';

interface ContactMethodModalProps {
  guestName: string;
  guestEmail: string;
  previousChannel?: 'email' | 'chat';
  isGuest?: boolean;
  onSelectEmail: () => void;
  onSelectChat: () => void;
  onClose: () => void;
}

export const ContactMethodModal: React.FC<ContactMethodModalProps> = ({
  guestName,
  guestEmail,
  previousChannel = 'email',
  isGuest = false,
  onSelectEmail,
  onSelectChat,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div
        className="w-full max-w-md bg-[#1C1C1E] border border-[#2C2C2E] rounded-[28px] overflow-hidden shadow-2xl p-6"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-[#2C2C2E]">
          <div>
            <h3 className="text-white text-base font-bold">Contact {guestName}</h3>
            <p className="text-xs text-zinc-400">Choose how you would like to connect</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white rounded-full hover:bg-white/10 transition"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Channels List */}
        <div className="space-y-3 py-5">
          {/* Email Option */}
          <button
            onClick={() => {
              onSelectEmail();
              onClose();
            }}
            className="group flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E] bg-[#2A2A2D] p-4 text-left transition hover:border-[#1784FF] hover:bg-[#1B2B4B]/50 cursor-pointer"
          >
            <div className="flex items-start gap-3.5 min-w-0">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600/20 text-[#1784FF]">
                <FiMail size={20} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-white text-sm">Send Email</span>
                  {previousChannel === 'email' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold text-blue-400">
                      <FiCheckCircle size={10} />
                      Last used
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 truncate">
                  Send email to <span className="text-zinc-200">{guestEmail}</span>
                </p>
              </div>
            </div>

            <FiChevronRight
              size={18}
              className="text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0 ml-2"
            />
          </button>

          {/* Chat Option */}
          <button
            onClick={() => {
              if (isGuest) return;
              onSelectChat();
              onClose();
            }}
            disabled={isGuest}
            className={
              isGuest
                ? 'group flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E]/40 bg-[#2A2A2D]/40 p-4 text-left opacity-40 cursor-not-allowed'
                : 'group flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E] bg-[#2A2A2D] p-4 text-left transition hover:border-[#00D45A] hover:bg-[#1A3A28]/50 cursor-pointer'
            }
          >
            <div className="flex items-start gap-3.5 min-w-0">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/20 text-[#00D45A]">
                <FiMessageSquare size={20} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-white text-sm">Direct Chat</span>
                  {isGuest ? (
                    <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                      Guest
                    </span>
                  ) : previousChannel === 'chat' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">
                      <FiCheckCircle size={10} />
                      Last used
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-[#00D45A]">
                      Real-time
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400">
                  {isGuest
                    ? 'Direct chat is only available for registered users.'
                    : 'Instant live messaging & terms negotiation'}
                </p>
              </div>
            </div>

            {!isGuest && (
              <FiChevronRight
                size={18}
                className="text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0 ml-2"
              />
            )}
          </button>
        </div>

        {/* Footer info */}
        <div className="pt-3 border-t border-[#2C2C2E] text-center">
          <p className="text-[11px] text-zinc-500">
            You can continue via email or switch to chat at any time.
          </p>
        </div>
      </div>
    </div>
  );
};
